/*
 * The brand colour: the ONE place to change it.
 *
 * Everything that follows the brand reads it from here. Every `*-brand-*`
 * Tailwind class points at the CSS variables `--brand-50` … `--brand-950`
 * (see tailwind.config.js), the stylesheets use them as rgb(var(--brand-600)),
 * and canvas code (charts, confetti) asks brandColor() for the same shades.
 * applyBrandColor() writes the variables on <html> before the app mounts.
 *
 * One colour becomes all eleven shades. Each shade keeps the WCAG luminance of
 * the ramp the app's colour pairings were tuned against. Only its hue and
 * saturation come from the chosen colour, so ANY colour stays readable: white
 * on 600 is 4.7:1, white on 700 6.6:1, 700 on 50 6.1:1, 300 on the dark
 * theme's gray-900 about 9:1.
 *
 * People can pick their own colour in Account → Appearance. That choice is
 * kept per browser (saveBrandColor), the same way the dark-mode choice is.
 */

/** Change this to rebrand the whole panel. */
export const DEFAULT_BRAND_COLOR = '#16807a'

/* The suggestions in Account → Appearance (anyone can still type any colour).
   None shares a hue with a status colour, so a primary button never looks
   like a Delete (red), Pause (amber) or success (green) button: red, orange
   and green presets measured 0.01–0.04 OKLab ΔE from those. Every button
   shade here is at least 0.08 from them and from each other. Literal colours,
   not DEFAULT_BRAND_COLOR, so a label cannot go stale if the default moves. */
export const BRAND_PRESETS = [
  { id: 'teal', label: 'Teal', color: '#16807a' },
  { id: 'sky', label: 'Sky', color: '#0284c7' },
  { id: 'blue', label: 'Blue', color: '#2563eb' },
  { id: 'violet', label: 'Violet', color: '#7c3aed' },
  { id: 'mauve', label: 'Mauve', color: '#8f5cbc' },
  { id: 'fuchsia', label: 'Fuchsia', color: '#c026d3' },
  { id: 'pink', label: 'Pink', color: '#db2777' },
  { id: 'graphite', label: 'Graphite', color: '#4b5563' }
]

export const BRAND_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950]

export const brandColorKey = 'brandColor'

/* The contrast contract: the relative luminance of each shade, taken from the
   mauve ramp every existing pairing was checked against. */
const LUMINANCE = [0.9162, 0.8216, 0.6756, 0.4807, 0.2908, 0.2204, 0.1712, 0.1083, 0.0707, 0.0487, 0.0209]

/* How saturated each shade is relative to the chosen colour (OKLCH chroma),
   measured from the default teal ramp. A greyish pick gives a greyish ramp. */
const CHROMA = [0.15, 0.36, 0.71, 1.17, 1.17, 1.08, 1, 0.88, 0.78, 0.69, 0.53]

const toLinear = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)
const toGamma = (c) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055)

function linearRgbToOklab(r, g, b) {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  ]
}

function oklabToLinearRgb(L, a, b) {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s
  ]
}

const luminance = ([r, g, b]) => 0.2126 * r + 0.7152 * g + 0.0722 * b
const inGamut = (rgb) => rgb.every((c) => c >= -1e-4 && c <= 1 + 1e-4)

/* The OKLab point with this chroma and hue whose luminance is `target`. */
function atLuminance(chroma, hue, target) {
  const a = chroma * Math.cos(hue)
  const b = chroma * Math.sin(hue)
  let lo = 0
  let hi = 1
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2
    if (luminance(oklabToLinearRgb(mid, a, b)) < target) lo = mid
    else hi = mid
  }
  return oklabToLinearRgb((lo + hi) / 2, a, b)
}

/* Keeps the luminance and gives up saturation where the screen cannot show
   the full chroma (a dark yellow, a very light blue). */
function shade(chroma, hue, target) {
  const full = atLuminance(chroma, hue, target)
  if (inGamut(full)) return full

  let best = atLuminance(0, hue, target)
  let lo = 0
  let hi = chroma
  for (let i = 0; i < 30; i++) {
    const mid = (lo + hi) / 2
    const candidate = atLuminance(mid, hue, target)
    if (inGamut(candidate)) {
      best = candidate
      lo = mid
    } else {
      hi = mid
    }
  }
  return best
}

/** `#rgb` / `#rrggbb` (with or without the #) → `#rrggbb`, anything else → null. */
export function normalizeHex(value) {
  if (typeof value !== 'string') return null
  const match = value.trim().match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i)
  if (!match) return null
  const digits = match[1].toLowerCase()
  return `#${digits.length === 3 ? digits.replace(/./g, '$&$&') : digits}`
}

/** `{ 50: [r, g, b], …, 950: [r, g, b] }` for any colour. */
export function brandRamp(color) {
  const hex = normalizeHex(color) ?? DEFAULT_BRAND_COLOR
  const [r, g, b] = [1, 3, 5].map((i) => toLinear(parseInt(hex.slice(i, i + 2), 16) / 255))
  const [, a, bb] = linearRgbToOklab(r, g, b)
  const chroma = Math.hypot(a, bb)
  const hue = Math.atan2(bb, a)

  const ramp = {}
  BRAND_STEPS.forEach((step, i) => {
    ramp[step] = shade(chroma * CHROMA[i], hue, LUMINANCE[i]).map((c) =>
      Math.round(toGamma(Math.min(1, Math.max(0, c))) * 255)
    )
  })
  return ramp
}

let applied = null

/** Writes the ramp for `color` as the `--brand-*` variables on <html>. */
export function applyBrandColor(color) {
  const hex = normalizeHex(color) ?? DEFAULT_BRAND_COLOR
  const ramp = brandRamp(hex)
  const { style } = document.documentElement
  BRAND_STEPS.forEach((step) => style.setProperty(`--brand-${step}`, ramp[step].join(', ')))
  applied = { hex, ramp }
  return hex
}

/** The colour in use now, as `#rrggbb`. */
export const currentBrandColor = () => applied?.hex ?? DEFAULT_BRAND_COLOR

const currentShade = (step) => {
  const ramp = applied?.ramp ?? brandRamp(DEFAULT_BRAND_COLOR)
  return ramp[step] ?? ramp[600]
}

/** One shade of the current brand as a CSS colour, for code that cannot use
    CSS variables (canvas: charts). */
export function brandColor(step, alpha = 1) {
  const [r, g, b] = currentShade(step)
  return alpha === 1 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/** One shade as `#rrggbb`, for libraries that read hex only (canvas-confetti
    strips everything that is not a hex digit, so an rgb() string turns red). */
export const brandHex = (step) =>
  `#${currentShade(step).map((c) => c.toString(16).padStart(2, '0')).join('')}`

/** The brand's hue in degrees, as CSS hue-rotate() counts them (HSL), or null
    when the brand is too grey to have one. For tinting artwork to match. */
export function brandHue() {
  const [r, g, b] = currentShade(500).map((c) => c / 255)
  const max = Math.max(r, g, b)
  const spread = max - Math.min(r, g, b)
  if (spread < 0.15) return null
  const sector = max === r ? ((g - b) / spread) % 6 : max === g ? (b - r) / spread + 2 : (r - g) / spread + 4
  return (sector * 60 + 360) % 360
}

/** This browser's saved choice, or the default. */
export function loadBrandColor() {
  try {
    return normalizeHex(localStorage.getItem(brandColorKey)) ?? DEFAULT_BRAND_COLOR
  } catch {
    return DEFAULT_BRAND_COLOR
  }
}

/** Saves and applies a choice. Null or the default clears the saved choice. */
export function saveBrandColor(color) {
  const hex = normalizeHex(color) ?? DEFAULT_BRAND_COLOR
  try {
    if (hex === DEFAULT_BRAND_COLOR) localStorage.removeItem(brandColorKey)
    else localStorage.setItem(brandColorKey, hex)
  } catch {
    // Storage blocked (private mode): the choice lasts for this page load.
  }
  return applyBrandColor(hex)
}
